import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreatePaymentTermPayload,
  type IPaymentTermRepository,
  type UpdatePaymentTermPayload,
} from '../../../business-logic/interfaces/IPaymentTermRepository'
import { type PaymentTermConstructor } from '../../../entities/PaymentTerm'
import { type PaymentTermApiModel } from '../../api-models/PaymentTermApiModel'
import { mapPaymentTermApiModel } from '../../mappers/mapPaymentTermApiModel'
import { type GetPaymentTermsResponse } from '../../types/GetPaymentTermsResponse'

const PAYMENT_TERM_URL = '/api/v3/terms/payment/'

interface Dependency {
  httpClient: IHttpClient
}

export class PaymentTermRepository implements IPaymentTermRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<PaymentTermConstructor>> {
    const res = await this.deps.httpClient.get<GetPaymentTermsResponse>(
      PAYMENT_TERM_URL,
      {
        params: formatRepoFiltersToApiFilters(params),
      }
    )

    const paymentTerms = res.data.data || []
    const metadata = res.data.metadata

    return {
      data: paymentTerms.map((paymentTerm) =>
        mapPaymentTermApiModel(paymentTerm)
      ),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  // Omits Angular's stray `id: null` / `text: ""` create fields — the server
  // ignores them
  async create(payload: CreatePaymentTermPayload): Promise<{ id: number }> {
    try {
      const res = await this.deps.httpClient.post<{
        data: PaymentTermApiModel
      }>(PAYMENT_TERM_URL, payload)

      return { id: res.data.data.id }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(id: number, payload: UpdatePaymentTermPayload): Promise<void> {
    try {
      // PUT the whole row (Angular parity)
      await this.deps.httpClient.put(`${PAYMENT_TERM_URL}${id}/`, {
        id,
        ...payload,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Canonical trailing slash — avoids the 301 hop Angular incurs
  async delete(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${PAYMENT_TERM_URL}${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
